import { type SanityImageSource } from "@sanity/image-url/lib/types/types";
import { groq } from "next-sanity";
import { getClient } from "../sanity";

export type Author = {
  name: string;
  picture?: SanityImageSource;
};

export type BlogSimple = {
  title: string;
  slug: string;
  image: SanityImageSource;
  imageUrl: string;
  excerpt: string;
  publishedAt: string;
};

export const blogSimpleFragment = "title, slug, image, excerpt, publishedAt, 'imageUrl': image.asset->url";

export const getBlogs = async (slugs: string[]): Promise<BlogSimple[]> => {
  const query = groq`
    *[_type == "blog" && !hidden && slug in [${slugs.map((slug) => "'" + slug + "'")}]] {
      ${blogSimpleFragment}
    }
  `;
  const unorderedBlogs = await getClient().fetch(query);
  // Return related blogs in the selected order, and filter out any undefined values.
  return slugs.map((slug) => unorderedBlogs.find((blog: BlogSimple) => blog.slug === slug)).filter(Boolean);
};

export type Blog = {
  title: string;
  slug: string;
  image: SanityImageSource;
  imageAlt: string;
  excerpt: string;
  content: string;
  publishedAt: string;
  authors: Author[];
  relatedBlogs?: string[];
  metaTitle?: string;
  metaDescription?: string;
  metaImageUrl?: string;
  personaTag?: string;
};

const blogFragment =
  "title, slug, content, excerpt, publishedAt, image, imageAlt, authors[]->, 'relatedBlogs': resources[]->slug, metaTitle, metaDescription, 'metaImageUrl': metaImage.asset->url, personaTag";

export const getBlogPost = async (slug: string): Promise<Blog> => {
  const query = groq`
    *[_type == "blog" && slug == "${slug}"][0] {
      ${blogFragment}
    }
  `;
  const blogPost = await getClient().fetch(query);
  return blogPost;
};
